import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

import type {
  Allocation,
  Portfolio,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DataTable } from '#/components/data/DataTable';
import { NoData } from '#/components/ui/NoData';
import { PercentBar } from '#/components/ui/PercentBar';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { Tabs } from '#/components/ui/Tabs';
import { SECTOR_SLUG_BY_LABEL } from '#/lib/category-navigation';
import { formatPercent } from '#/utils/format';

function getColumns(firstColumnHeader: string): ColumnDef<Allocation>[] {
  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: firstColumnHeader,
      enableSorting: false,
      cell: ({ getValue }) => {
        const name = getValue<string | null>() ?? null;

        if (!name) {
          return <NoData />;
        }

        if (firstColumnHeader !== 'Sector') {
          return name;
        }

        const sectorSlug = SECTOR_SLUG_BY_LABEL[name];

        if (!sectorSlug) {
          return name;
        }

        return (
          <Link to="/$category" params={{ category: sectorSlug }}>
            {name}
          </Link>
        );
      },
    },
    {
      id: 'bar',
      header: () => <span className="sr-only">Share of portfolio</span>,
      enableSorting: false,
      size: 300,
      minSize: 100,
      maxSize: Number.MAX_SAFE_INTEGER,
      cell: ({ row }) =>
        row.original.percent != null ? (
          <PercentBar
            percent={row.original.percent}
            label={`${row.original.name ?? 'Allocation'}, ${row.original.percent}% of portfolio`}
          />
        ) : null,
    },
    {
      accessorKey: 'percent',
      header: 'Net',
      // Rows arrive pre-sorted largest-first; no interactive sort control.
      enableSorting: false,
      meta: { align: 'end' },
      cell: ({ getValue }) =>
        formatPercent(getValue<number | null>()) ?? <NoData />,
    },
  ];
}

interface PortfolioSectionProps {
  data: Portfolio | null | undefined;
  description?: string | null;
}

export function PortfolioSection({ data, description }: PortfolioSectionProps) {
  const panels = [
    {
      id: 'asset-class',
      label: 'Asset Class',
      allocations: data?.asset_class_allocations,
    },
    {
      id: 'sector',
      label: 'Sector',
      allocations: data?.sector_allocations,
    },
  ].filter((panel) => (panel.allocations?.length ?? 0) > 0);

  const [activeTab, setActiveTab] = useState(panels[0]?.id);

  if (panels.length === 0) {
    return null;
  }

  const activePanel =
    panels.find((panel) => panel.id === activeTab) ?? panels[0];

  if (!activePanel) {
    return null;
  }

  // Largest share on top; allocations missing a percent sort to the bottom.
  const sortedAllocations = [...(activePanel.allocations ?? [])].sort(
    (a, b) => (b.percent ?? -Infinity) - (a.percent ?? -Infinity),
  );
  const columns = getColumns(activePanel.label);

  return (
    <section
      id="portfolio"
      className="flow flow-inner-8 scroll-mt-(--scroll-offset)"
    >
      <SectionHeader title="Portfolio" description={description} />

      <div className="flow flow-20 p-gap-inset border-divider-subtle -mx-gap border-t border-b sm:mx-0 sm:rounded sm:border">
        <Tabs
          aria-label="Portfolio allocations"
          tabs={panels.map(({ id, label }) => ({
            id,
            label,
            controls: `${id}-panel`,
          }))}
          activeTab={activePanel.id}
          onChange={setActiveTab}
        />

        <div
          role="tabpanel"
          id={`${activePanel.id}-panel`}
          aria-labelledby={activePanel.id}
        >
          <DataTable
            aria-label={`${activePanel.label} allocation`}
            columns={columns}
            data={sortedAllocations}
          />
        </div>
      </div>
    </section>
  );
}
